import { lang } from '@umu/ukit-lang';
/*
 * @author: wayou
 * @date: 2018-12-10 10:54:20
 * @description: 作业 AI 报告聚合组件，包含雷达图，关键词，词云的展示
 */

import React, { Component } from 'react';
import {
    IKeywordItem,
    IHomeworkInfo,
    IResponse,
    KEYWORD_TASK_STATUS,
    HomeworkType as homeworkTypeMap,
    IElementSetup,
    HomeworkTypeBySession,
} from '@umu/umu-types';
import { IAiReportData } from '@umu/umu-types/src/interface/aiReportInfo';
import KeywordsSection, { AI_KEYWORD_RULE } from '../@keywordsSection/keywordsSection';
import KeyWrodsEmpty from '../@keyWrodsEmpty/keyWrodsEmpty';
import TextHighlightSection from '../@textHighlightSection/textHighlightSection';
import { IGetKeywordsResponse } from '@umu/umu-api/src/services/api/getHomeworkKeywords';

import { getHomeworkKeywords } from '@umu/umu-api';
import AIReport from '../../components/@aiReport/aiReport';
import './aiTab.scss';

interface IAiTabProps {
    homeworkId: number;
    studentId: string;
    aiVoiceCheckSwitch: number;
    aiWeight: string;
    expressiveMinScore?: number;
    expressiveFullScore?: number;
    keywordMinScore?: number;
    keywordFullScore?: number;
    aiKeywordMatchRule?: AI_KEYWORD_RULE;
    sessionSetupInfo?: IElementSetup;
    ytmw_def_val?: number;
    allow_ytmw_cal_to_score?: number;
    expressivenessTaskStatus?: number;
}

interface IAiTabStates {
    keywords: IKeywordItem[];
    frequencyWords: IKeywordItem[];
    text: string;
    keywordCoverage: number;
    aiReportInfo?: IAiReportData;
    isProcessing: KEYWORD_TASK_STATUS;
    isLoading: boolean;
    homeworkInfo?: IHomeworkInfo;
    homeworkType: homeworkTypeMap;
    aiKeywordEvaluationScore: number;
    highlight: string[];
}

let requestCache: IResponse<IGetKeywordsResponse>;

export default class AiTab extends Component<IAiTabProps, IAiTabStates> {
    constructor(props: IAiTabProps) {
        super(props);
        this.state = {
            isProcessing: KEYWORD_TASK_STATUS.RUNNING,
            keywords: [],
            frequencyWords: [],
            text: '',
            keywordCoverage: 0,
            isLoading: true,
            homeworkType: homeworkTypeMap.AUDIO,
            aiKeywordEvaluationScore: 0,
            highlight: [],
        };
    }

    public componentDidMount() {
        this.getKeywords();
    }

    public render() {
        const { keywords, frequencyWords, text, isProcessing, aiReportInfo } = this.state;
        const isAIEmpty =
            !text &&
            isProcessing !== KEYWORD_TASK_STATUS.RUNNING &&
            keywords.length === 0 &&
            frequencyWords.length === 0 &&
            (!aiReportInfo || !aiReportInfo.ai_score);
        if (!isAIEmpty) {
            return (
                <div className="ai-tab">
                    {this.renderAiReport()}
                    {this.renderKeywords()}
                    {this.renderHighlights()}
                </div>
            );
        } else {
            return <div className="ai-tab">{this.renderAIEmpty()}</div>;
        }
    }

    private renderAiReport() {
        const {
            homeworkId,
            aiVoiceCheckSwitch,
            aiWeight,
            expressiveMinScore = 0,
            sessionSetupInfo,
            expressiveFullScore = 5,
            allow_ytmw_cal_to_score,
            ytmw_def_val,
            expressivenessTaskStatus,
        } = this.props;
        const { homeworkInfo, aiReportInfo } = this.state;
        const hasExpressive = sessionSetupInfo?.ai_switch == '1';
        if(homeworkInfo?.homework_type !== HomeworkTypeBySession.VIDEO) {
            return null;
        }
        if (!hasExpressive) {
            return null;
        }

        if (hasExpressive && expressivenessTaskStatus === 0) {
            return null;
        }

        if (hasExpressive && (!aiReportInfo || !aiReportInfo.ai_score)) {
            return (
                <div className="ai-tab-section">
                    <div className="ai-tab-header">
                        <div className="title">{lang('表现力')}</div>
                    </div>
                    <KeyWrodsEmpty title={lang('AI识别中，稍后再来')}/>
                </div>
            );
        }
        if (!aiReportInfo) {
            return null;
        }

        aiReportInfo.ai_weight = aiWeight;

        let userName = '';
        if (homeworkInfo && homeworkInfo.user_info !== undefined) {
            userName = /*老接口中为 name 字段而非标准结构中的 user_name */ (homeworkInfo.user_info as any).name;
        }
        return (
            <AIReport
                aiVoiceCheckSwitch={aiVoiceCheckSwitch}
                homeworkId={homeworkId}
                userName={userName}
                aiReportInfo={aiReportInfo}
                expressiveMinScore={expressiveMinScore}
                expressiveFullScore={expressiveFullScore}
                allow_ytmw_cal_to_score={allow_ytmw_cal_to_score}
                ytmw_def_val={ytmw_def_val}
            />
        );
    }

    private renderKeywords() {
        const { keywordMinScore, keywordFullScore, aiKeywordMatchRule, sessionSetupInfo } = this.props;
        const { keywords, frequencyWords, isProcessing, aiKeywordEvaluationScore, homeworkInfo } = this.state;
        // 关键词 如果没有则不出
        if (keywords.length === 0 && frequencyWords.length === 0) {
            return null;
        }

        return (
            <div className="ai-tab-section homework-keywords">
                <KeywordsSection
                    isProcessing={isProcessing === KEYWORD_TASK_STATUS.RUNNING}
                    keywords={keywords}
                    frequencyWords={frequencyWords}
                    keywordTitle={lang('作业未设置关键词')}
                    keywordSubTitle={lang('关键词统计结果将展示在这里')}
                    keywordMinScore={keywordMinScore}
                    keywordFullScore={keywordFullScore}
                    aiKeywordMatchRule={aiKeywordMatchRule}
                    aiKeywordEvaluationScore={aiKeywordEvaluationScore}
                    setupInfo={sessionSetupInfo}
                    homeworkInfo={homeworkInfo}
                />
            </div>
        );
    }

    private renderHighlights() {
        const { homeworkType, highlight, text, isProcessing } = this.state;
        if (homeworkType === homeworkTypeMap.IMGTXT) {
            return null;
        }
        // 转文字 如果没有则不出
        if (!text && isProcessing !== KEYWORD_TASK_STATUS.RUNNING) {
            return null;
        }
        return (
            <div className="ai-tab-section homework-highlights">
                <div className="ai-tab-header">
                    <div className="title">{lang('转文字')}</div>
                </div>
                {!text && isProcessing !== KEYWORD_TASK_STATUS.RUNNING ? (
                    <KeyWrodsEmpty />
                ) : (
                    <TextHighlightSection
                        isProcessing={isProcessing === KEYWORD_TASK_STATUS.RUNNING}
                        highlight={highlight}
                        text={text}
                        setupInfo={this.props.sessionSetupInfo}
                    />
                )}
            </div>
        );
    }

    private renderAIEmpty() {
        return (
            <div className="ai-tab-section homework-highlights">
                <div className="ai-tab-header">
                    <div className="title">{lang('AI分析')}</div>
                </div>
                <KeyWrodsEmpty title={lang('暂时没有AI分析')} />
            </div>
        );
    }
    private getKeywords() {
        if (requestCache) {
            this.fetchSuccess(requestCache);
            console.warn('skip request');
            return;
        }
        const { homeworkId, studentId } = this.props;
        const params = {
            homework_id: homeworkId,
            student_id: studentId,
            return_text: 1,
        };
        getHomeworkKeywords(params).then((res) => {
            requestCache = res;
            this.fetchSuccess(res);
        });
    }

    private fetchSuccess(res: IResponse<IGetKeywordsResponse>) {
        if (res.error_code) {
            return;
        }
        this.setState({
            highlight: res.data.highlight,
            isProcessing: res.data.keywords_task_status,
            isLoading: false,
            keywords: res.data.keywords.list,
            frequencyWords: res.data.frequency_words.list,
            text: res.data.text,
            keywordCoverage: res.data.keywords.keyword_coverage,
            aiReportInfo: res.data.ai_report_info,
            homeworkInfo: res.data.homework_info,
            homeworkType: res.data.homework_type,
            aiKeywordEvaluationScore: res.data.homework_info.ai_keyword_evaluation_score || 0,
        });
    }
}
